import React, { useState } from 'react';
import { Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { historyData } from '../data/history';

const HistoryTimelineSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timeline = historyData.timeline;

  const handlePrev = () => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : timeline.length - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev < timeline.length - 1 ? prev + 1 : 0));
  };

  return (
    <section id="history" className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-teal-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-amber-100/50 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4">
            <Clock className="w-4 h-4" />
            Our Journey
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            {historyData.title}
          </h2>
          <p className="text-slate-600 max-w-3xl mx-auto text-lg">
            {historyData.subtitle}
          </p>
        </div>

        {/* Timeline Navigation */}
        <div className="flex items-center justify-center gap-2 mb-8 overflow-x-auto pb-4">
          {timeline.map((item, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeIndex === index
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {item.year}
            </button>
          ))}
        </div>

        {/* Active Timeline Item */}
        <div className="bg-slate-50 rounded-3xl p-8 lg:p-12 border border-slate-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={timeline[activeIndex].image}
                  alt={timeline[activeIndex].title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-teal-600 text-white px-6 py-3 rounded-xl font-bold text-xl shadow-lg">
                {timeline[activeIndex].year}
              </div>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-800 mb-4">
                {timeline[activeIndex].title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                {timeline[activeIndex].description}
              </p>
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={handlePrev}
              className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-600 transition-all duration-300"
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <span className="text-slate-500 text-sm">
              {activeIndex + 1} of {timeline.length}
            </span>
            <button
              onClick={handleNext}
              className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-600 transition-all duration-300"
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HistoryTimelineSection;
